import type { FileRecord } from "@/shared/domain/files";

/**
 * Where a URDF's generated glTF lives, derived from the source file's record.
 *
 * ## Why this is duplicated from Python
 *
 * `packages/roboto/src/roboto/urdf/asset_paths.py` computes the same strings,
 * and this module is a deliberate, exact re-implementation of it rather than a
 * value fetched from the service.
 *
 * The wizard has to know where the artifact *would* be **before** it makes any
 * request: it is what lets the dialog look for an existing asset without first
 * asking the platform where one would go. It is also the fallback for a status
 * block that does not name its artifact, so the app is never left holding a
 * conversion it cannot address.
 *
 * The cost of the duplication is that the two implementations must not drift.
 * Both are pinned by a table of the same cases — see `visualizationAssetPaths.test.ts`
 * here and `packages/roboto/test/urdf/test_asset_paths.py` there — so a change to
 * either contract fails the other's tests.
 *
 * ## Why the leaf is keyed on `upload_id`, not `version`
 *
 * `version` is a record-revision counter: file records are append-only, so *any*
 * write to the record — a tag, a metadata edit, or the worker recording its own
 * status onto the source file — inserts a new row and moves it. A path keyed on
 * it stops being derivable the moment anything touches the record, and the
 * status write moves it as a matter of course.
 *
 * `upload_id` identifies the upload transaction instead: it survives every
 * metadata edit, and changes if and only if the bytes are genuinely re-uploaded.
 * That makes it both a stable key for the path and the identity a status block
 * can be checked against for staleness — see `ingestPhase`.
 */

/** Hidden directory holding platform-generated viewing artifacts. */
export const VISUALIZATION_ASSETS_PREFIX = ".VISUALIZATION_ASSETS";

/** Producer-specific subdirectory for glTFs converted from source models. */
export const GLTF_ASSET_DIRECTORY = `${VISUALIZATION_ASSETS_PREFIX}/gltf`;

/**
 * The fields of a source file record a canonical path is derived from.
 *
 * Narrower than `FileRecord` so callers with a partial record — a test fixture,
 * a row from a listing — can derive a path without fabricating the rest.
 */
export type VisualizationAssetSource = Pick<
  FileRecord,
  "file_id" | "relative_path" | "upload_id"
>;

/** Every character the path segment may not contain. */
const UNSAFE_STEM_CHARACTER = /[^A-Za-z0-9\-_.]/g;

/** Leading and trailing `.`/`_`, which Python's `str.strip("._")` removes. */
const TRIMMABLE_STEM_EDGES = /^[._]+|[._]+$/g;

/**
 * Reduce an arbitrary string to something safe to use as one path segment.
 *
 * Mirrors `sanitize_stem` in Python: every character outside `[A-Za-z0-9-_.]`
 * becomes `_`, leading and trailing `.`/`_` are stripped, and a stem that
 * survives none of that falls back to `robot`.
 */
export function sanitizeStem(text: string): string {
  const cleaned = text.replace(UNSAFE_STEM_CHARACTER, "_");
  return cleaned.replace(TRIMMABLE_STEM_EDGES, "") || "robot";
}

/**
 * The filename stem of a POSIX path, matching `PurePosixPath(...).stem`.
 *
 * Only the last segment is used, and the extension is only stripped when the
 * dot is neither the first nor the last character — so `.urdf` keeps its name
 * (a dotfile has no extension) and `....urdf` reduces to `...`, which then
 * sanitizes to the fallback.
 */
function posixStem(relativePath: string): string {
  const name =
    relativePath
      .split("/")
      .filter((segment) => segment !== "")
      .at(-1) ?? "";
  const dot = name.lastIndexOf(".");
  return dot > 0 && dot < name.length - 1 ? name.slice(0, dot) : name;
}

/**
 * The dataset-relative path of the glTF generated from this source file.
 *
 * The shape is `.VISUALIZATION_ASSETS/gltf/<stem>/<file_id>_<upload_id>.gltf`.
 * The stem is the sanitized source filename and exists purely for human
 * readability; uniqueness comes entirely from the file id in the leaf, so two
 * identically named URDFs in different directories cannot collide.
 *
 * Legacy records carry `upload_id` `"NO_ID"` — a value like any other here, and
 * the path stays derivable for them.
 */
export function canonicalAssetPath(
  fileRecord: VisualizationAssetSource,
): string {
  const stem = sanitizeStem(posixStem(fileRecord.relative_path));
  return `${GLTF_ASSET_DIRECTORY}/${stem}/${fileRecord.file_id}_${fileRecord.upload_id}.gltf`;
}

/**
 * The glTF's sidecar `.bin` buffer.
 *
 * A sibling of the `.gltf` sharing its stem, which is what lets the exported
 * `buffers[].uri` be a bare filename that a consumer resolves by sibling lookup.
 */
export function canonicalBufferPath(
  fileRecord: VisualizationAssetSource,
): string {
  return withExtension(canonicalAssetPath(fileRecord), ".bin");
}

function withExtension(assetPath: string, extension: string): string {
  return `${assetPath.slice(0, -".gltf".length)}${extension}`;
}
